import React, { useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { HotelContext } from "../../context/HotelContext";
import { getLocale, translate } from "../../utils/Translation";
import { formatUtilityPrice } from "../../components/v2/cards/HotelUtilityPricesCard";
import waterIcon from "../../img/v2/mdi_water.svg";
import savingsPotIcon from "../../img/v2/tdesign_saving-pot-filled.svg";
import electricityIcon from "../../img/v2/mage_electricity-fill.svg";

const CLOUD7_ALULA_HID = 37;

const ExplainerIcon = ({ src, alt = "" }) => (
  <img src={src} alt={alt} className="actions-water__explainer-icon" aria-hidden="true" />
);

const ActionsWaterSavingsExplanation = () => {
  const keycloak = useAuth();
  const { hId, lang } = useContext(HotelContext);
  const locale = getLocale(lang);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [pricesError, setPricesError] = useState(false);
  const [prices, setPrices] = useState({
    waterPricePerM3: null,
    energyPricePerKwh: null,
  });

  useEffect(() => {
    if (!keycloak?.isAuthenticated || !keycloak.user?.access_token || !hId) {
      setPricesLoading(false);
      setPricesError(!hId);
      return;
    }

    let cancelled = false;
    setPricesLoading(true);
    setPricesError(false);

    fetch(`/api/hotels/${hId}/water/utility-prices`, {
      method: "GET",
      headers: {
        Authorization: "Bearer " + keycloak.user.access_token,
        "x-front-token": "Bs#PSQyhy)!y9Ti{",
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        if (data.success !== "OK") {
          throw new Error(data.message || "Fetch failed");
        }
        setPrices({
          waterPricePerM3: data.waterPricePerM3,
          energyPricePerKwh: data.energyPricePerKwh,
        });
        setPricesError(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("ActionsWaterSavingsExplanation:", err);
        setPricesError(true);
      })
      .finally(() => {
        if (!cancelled) {
          setPricesLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [keycloak?.isAuthenticated, keycloak.user?.access_token, hId]);

  const isCloud7AlUla = Number(hId) === CLOUD7_ALULA_HID;
  const currency = isCloud7AlUla ? "SAR" : "EUR";

  return (
    <section
      className="actions-water__info-card actions-water__explainer"
      aria-labelledby="actions-water-explainer-title"
    >
      <h2 id="actions-water-explainer-title" className="actions-water__explainer-title">
        {translate("v2_water_explainer_title", lang)}
      </h2>

      <div className="actions-water__explainer-body">
        <div className="actions-water__explainer-formula">
          <ExplainerIcon src={savingsPotIcon} />
          <div className="actions-water__explainer-formula-text">
            <strong className="actions-water__explainer-formula-savings">
              {translate("v2_savings_word", lang)}
            </strong>
            <span className="actions-water__explainer-formula-operator">=</span>
            <ExplainerIcon src={waterIcon} />
            <strong className="actions-water__explainer-formula-water">
              {translate("v2_water_explainer_water_saved", lang)}
            </strong>
            <span className="actions-water__explainer-formula-operator">x</span>
            <strong>{translate("v2_water_explainer_cost_water", lang)}</strong>
            <span className="actions-water__explainer-formula-operator">+</span>
            <ExplainerIcon src={electricityIcon} />
            <strong className="actions-water__explainer-formula-energy">
              {translate("v2_water_explainer_energy_saved", lang)}
            </strong>
            <span className="actions-water__explainer-formula-operator">x</span>
            <strong>{translate("v2_water_explainer_cost_energy", lang)}</strong>
          </div>
        </div>

        <div className="actions-water__explainer-columns">
          <div className="actions-water__explainer-detail">
            <div className="actions-water__explainer-detail-header">
              <ExplainerIcon src={waterIcon} />
              <h3 className="actions-water__explainer-detail-title">
                {translate("v2_water_explainer_calc_water_prefix", lang)}
                <span className="actions-water__explainer-formula-water">
                  {translate("v2_water_explainer_water_saved", lang)}
                </span>
              </h3>
            </div>
            <p className="actions-water__explainer-detail-text">
              {translate("v2_water_explainer_water_description", lang)}
            </p>
            <p className="actions-water__explainer-detail-note">
              {translate("v2_water_explainer_water_witness_note", lang)}
            </p>
          </div>

          <div className="actions-water__explainer-detail">
            <div className="actions-water__explainer-detail-header">
              <ExplainerIcon src={electricityIcon} />
              <h3 className="actions-water__explainer-detail-title">
                {translate("v2_water_explainer_calc_energy_prefix", lang)}
                <span className="actions-water__explainer-formula-energy">
                  {translate("v2_water_explainer_energy_saved", lang)}
                </span>
              </h3>
            </div>
            <p className="actions-water__explainer-detail-text">
              {translate("v2_water_explainer_energy_description", lang)}
            </p>
            <p className="actions-water__explainer-detail-text">
              {translate("v2_water_explainer_energy_params_intro", lang)}
            </p>
            <ul className="actions-water__explainer-detail-list">
              <li>{translate("v2_water_explainer_cold_temp", lang)}</li>
              <li>{translate("v2_water_explainer_hot_temp", lang)}</li>
              <li>{translate("v2_water_explainer_heating_type", lang)}</li>
              <li>{translate("v2_water_explainer_energy_efficiency", lang)}</li>
            </ul>
          </div>
        </div>
      </div>

      <h3 className="actions-water__explainer-params-heading">
        {translate("v2_hotel_utility_title", lang)}
      </h3>

      <div className="actions-water__explainer-params-grid">
        <div className="actions-water__explainer-param actions-water__explainer-param--water">
          <p className="actions-water__explainer-param-label">
            {translate("v2_water_explainer_cost_water", lang)}
          </p>
          <p className="actions-water__explainer-param-value">
            {pricesLoading || pricesError
              ? "—"
              : formatUtilityPrice(prices.waterPricePerM3, currency, locale)}
          </p>
        </div>
        <div className="actions-water__explainer-param actions-water__explainer-param--energy">
          <p className="actions-water__explainer-param-label">
            {translate("v2_water_explainer_cost_energy", lang)}
          </p>
          <p className="actions-water__explainer-param-value">
            {pricesLoading || pricesError
              ? "—"
              : formatUtilityPrice(prices.energyPricePerKwh, currency, locale)}
          </p>
        </div>
      </div>
    </section>
  );
};

export default ActionsWaterSavingsExplanation;
